import React, { useState } from "react";
import { ChevronDownIcon, ChevronRightIcon, PlusIcon } from "@heroicons/react/24/outline";
import { Pencil, Trash2 } from "lucide-react";
import { canCreateAndEditCustomReports } from "../../utils/custom-report-permissions";

const SchemaBrowser = ({
  tables = [],
  functions = [],
  onColumnClick,
  onFunctionClick,
  onNewFunction,
  onEditFunction,
  onDeleteFunction,
  onSaveAsReport,
  searchValue,
  setSearchValue,
  currentUserId,
}) => {
  const [expandedTables, setExpandedTables] = useState({});
  const [expandedFunctions, setExpandedFunctions] = useState({});
  const [activeTab, setActiveTab] = useState("tables");

  const canEdit = canCreateAndEditCustomReports();

  const toggleTable = (tableName) => {
    setExpandedTables((prev) => ({
      ...prev,
      [tableName]: !prev[tableName],
    }));
  };

  const toggleFunction = (functionId) => {
    setExpandedFunctions((prev) => ({
      ...prev,
      [functionId]: !prev[functionId],
    }));
  };

  const filteredTables = tables.filter((table) =>
    table.table_name.toLowerCase().includes((searchValue || "").toLowerCase())
  );

  const filteredFunctions = functions.filter((fn) =>
    fn.name.toLowerCase().includes((searchValue || "").toLowerCase())
  );

  const getTypeColor = (type) => {
    if (type.includes("int") || type.includes("numeric") || type.includes("decimal")) {
      return "text-027A48";
    }
    if (type.includes("varchar") || type.includes("text") || type.includes("char")) {
      return "text-primary";
    }
    if (type.includes("timestamp") || type.includes("date") || type.includes("time")) {
      return "text-FFBA08";
    }
    if (type.includes("bool")) {
      return "text-F94B4B";
    }
    return "text-667085";
  };

  return (
    <div className="h-full flex flex-col bg-white border-r border-EAECF0">
      {/* Search */}
      <div className="p-3 border-b border-EAECF0">
        <input
          type="text"
          placeholder={activeTab === "tables" ? "Search tables..." : "Search functions..."}
          value={searchValue || ""}
          onChange={(e) => setSearchValue(e.target.value)}
          className="w-full px-3 py-2 border border-D0D5DD rounded-md text-xs focus:outline-none focus:border-primary"
        />
      </div>

      {/* Tabs */}
      <div className="flex border-b border-EAECF0">
        <button
          onClick={() => setActiveTab("tables")}
          className={`flex-1 px-3 py-2 text-xs fw_500 ${
            activeTab === "tables"
              ? "text-primary border-b-2 border-primary"
              : "text-667085 hover:text-344054"
          }`}
        >
          Tables
        </button>
        <button
          onClick={() => setActiveTab("functions")}
          className={`flex-1 px-3 py-2 text-xs fw_500 ${
            activeTab === "functions"
              ? "text-primary border-b-2 border-primary"
              : "text-667085 hover:text-344054"
          }`}
        >
          Functions
        </button>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-2">
        {activeTab === "tables" && (
          <>
            {filteredTables.length === 0 ? (
              <div className="text-xs text-667085 p-2">No tables found</div>
            ) : (
              filteredTables.map((table) => (
                <div key={table.table_name} className="mb-1">
                  <button
                    onClick={() => toggleTable(table.table_name)}
                    className="w-full flex items-center gap-1 px-2 py-1.5 text-xs fw_500 text-344054 hover:bg-f3f8ff rounded"
                  >
                    {expandedTables[table.table_name] ? (
                      <ChevronDownIcon className="w-3 h-3" />
                    ) : (
                      <ChevronRightIcon className="w-3 h-3" />
                    )}
                    <span className="truncate">{table.table_name}</span>
                  </button>
                  {expandedTables[table.table_name] && (
                    <div className="ml-4 border-l border-EAECF0 pl-2">
                      {table.columns.map((column) => (
                        <button
                          key={column.name}
                          onClick={() => onColumnClick(table.table_name, column.name)}
                          className="w-full flex items-center justify-between px-2 py-1 text-xs text-667085 hover:bg-f3f8ff hover:text-primary rounded"
                        >
                          <span className="truncate">{column.name}</span>
                          <span className={`text-xsm ${getTypeColor(column.type)}`}>
                            {column.type.split(" ")[0]}
                          </span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              ))
            )}
          </>
        )}

        {activeTab === "functions" && (
          <>
            {canEdit && (
              <button
                onClick={onNewFunction}
                className="w-full flex items-center gap-1 px-2 py-2 mb-2 text-xs fw_500 text-primary hover:bg-f3f8ff rounded border border-dashed border-primary"
              >
                <PlusIcon className="w-3 h-3" /> New Function
              </button>
            )}
            {filteredFunctions.length === 0 ? (
              <div className="text-xs text-667085 p-2">No functions found</div>
            ) : (
              filteredFunctions.map((fn) => (
                <div key={fn.function_id} className="mb-1">
                  <div className="flex items-center group">
                    <button
                      onClick={() => toggleFunction(fn.function_id)}
                      className="flex-1 flex items-center gap-1 px-2 py-1.5 text-xs fw_500 text-344054 hover:bg-f3f8ff rounded"
                    >
                      {expandedFunctions[fn.function_id] ? (
                        <ChevronDownIcon className="w-3 h-3" />
                      ) : (
                        <ChevronRightIcon className="w-3 h-3" />
                      )}
                      <span className="truncate">{fn.name}</span>
                    </button>
                    {canEdit && fn.created_by === currentUserId && (
                      <div className="hidden group-hover:flex items-center gap-1 pr-1">
                        <Pencil
                          className="text-667085 hover:text-primary cursor-pointer"
                          size={12}
                          onClick={(e) => {
                            e.stopPropagation();
                            onEditFunction(fn);
                          }}
                        />
                        <Trash2
                          className="text-667085 hover:text-F94B4B cursor-pointer"
                          size={12}
                          onClick={(e) => {
                            e.stopPropagation();
                            onDeleteFunction(fn);
                          }}
                        />
                      </div>
                    )}
                  </div>

                  {expandedFunctions[fn.function_id] && (
                    <div className="ml-4 border-l border-EAECF0 pl-2">
                      {/* Click to insert function name */}
                      <button
                        onClick={() => onFunctionClick(fn)}
                        className="w-full px-2 py-1 text-xs text-primary hover:bg-f3f8ff rounded text-left"
                      >
                        Insert function call
                      </button>
                      {onSaveAsReport && (
                        <button
                          onClick={() => onSaveAsReport(fn)}
                          className="w-full px-2 py-1 text-xs text-027A48 hover:bg-f3f8ff rounded text-left"
                        >
                          Save as Report
                        </button>
                      )}
                      {/* Description */}
                      {fn.description && (
                        <div className="px-2 py-1 text-xsm text-667085">
                          {fn.description}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default SchemaBrowser;
